import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AUTH_RC } from '@core/constants/routes.constants';
import { ProfileApi } from '@core/services/api/profile.api';
import { AuthApi } from '@core/services/api/auth.api';
import { AuthActions } from '@features/auth/store';
import { Store } from '@ngrx/store';
import { Environment } from '@core/models/environment.models';
import { ENVIRONMENT } from '@core/constants/platform/environment.token';
import { MatDialog } from '@angular/material/dialog';

const ACCESS_TOKEN_KEY: string = 'access_token';
const REFRESH_TOKEN_KEY: string = 'refresh_token';
const ACTIVITY_KEY: string = 'lastActivity';

@Injectable({
  providedIn: 'root',
})
export class AutoLogoutService {
  private timeoutId: number | NodeJS.Timeout | undefined;
  private readonly timeoutDuration = this.environment.authSessionTime;

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private profileApi: ProfileApi,
    private authApi: AuthApi,
    private store: Store,
    private dialog: MatDialog,
    @Inject(ENVIRONMENT) private environment: Environment
  ) {}

  public trackActivity() {
    this.initListener();
    this.resetTimer();

    // Listen to storage changes from other tabs
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === ACTIVITY_KEY) {
        this.resetTimer(false);
      }
    });
  }

  private debounce(func: () => void, wait: number) {
    let timeout: NodeJS.Timeout | undefined;
    return (...args: []) => {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  }

  private initListener() {
    const events = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    const debouncedReset = this.debounce(() => this.resetTimer(true), 400);

    events.forEach((event) => {
      window.addEventListener(event, debouncedReset);
    });
  }

  /**
   * @param broadcast Whether to update localStorage and notify other tabs
   */
  private resetTimer(broadcast: boolean = false) {
    clearTimeout(this.timeoutId);

    if (broadcast) {
      localStorage.setItem(ACTIVITY_KEY, Date.now().toString());
    }

    this.ngZone.runOutsideAngular(() => {
      this.timeoutId = setTimeout(() => {
        this.ngZone.run(() => this.logout());
      }, this.timeoutDuration);
    });
  }

  private reset(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('impersonation_backup');
    this.profileApi.user = null;
  }

  private logout() {
    const now = new Date();
    console.log(
      `${now.toLocaleTimeString()} - Logging the user out after ${this.timeoutDuration / 1000 / 60} minute(s)`
    );
    if (this.authApi.isImpersonating()) {
      this.authApi.stopImpersonation();
    }
    this.reset();
    this.router.navigate([`${AUTH_RC.root}/${AUTH_RC.children.logIn}`], {
      queryParams: { auto: 1 },
    });
    this.store.dispatch(AuthActions.logout());
    this.dialog.closeAll();
  }
}
